import { Button } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetLiveUpdatesLocationsQuery } from 'api/liveUpdates'
import type { LiveUpdatesLocation } from 'api/liveUpdates.types'
import Pagination from 'components/Pagination'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { FormControl } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'

const t = buildTranslate('live_updates.locations_modal')

const MODAL_Z_INDEX = 1200
const CONTENT_WIDTH = '520px'
const LIST_MAX_HEIGHT = '360px'
const ROW_PADDING = '12px 0'
// Matches the RestrictedCustomers search debounce (CLAUDE.md search precedent).
export const LOCATIONS_SEARCH_DEBOUNCE_MS = 300

const formattedAddress = (location: LiveUpdatesLocation): string =>
  `${location.street}, ${location.city}, ${location.state} ${location.zip}`

export interface LocationsModalProps {
  isOpen: boolean
  onClose: () => void
  configurationId: string
  label: string
  locationsCount: number
}

// FR-42 read-only locations browser. A debounced server-side search over the
// configuration's assigned locations, paginated. The query is skipped while
// closed so reopening a stale modal doesn't fire a request until it's visible.
const LocationsModal = ({
  isOpen,
  onClose,
  configurationId,
  label,
  locationsCount,
}: LocationsModalProps) => {
  const [css] = useStyletron()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isFetching, isError } = useGetLiveUpdatesLocationsQuery(
    { id: configurationId, query: search || undefined, page },
    { skip: !isOpen }
  )

  const [debouncedSetSearch, cancelDebouncedSearch] = useDebouncedCallback(
    (value: string) => {
      setSearch(value)
      setPage(1)
    },
    LOCATIONS_SEARCH_DEBOUNCE_MS
  )

  // Drop a pending debounce on unmount so we don't setState on an unmounted tree.
  useEffect(() => cancelDebouncedSearch, [cancelDebouncedSearch])

  // A pending debounced search must not survive a close — otherwise it fires
  // while closed (the query itself is skipped, but `search` still updates)
  // and can overwrite the very reset below if the modal reopens inside the
  // debounce window, silently re-filtering behind an input that looks empty.
  useEffect(() => {
    if (!isOpen) cancelDebouncedSearch()
  }, [isOpen, cancelDebouncedSearch])

  // Reset the search + page when the modal reopens or the configuration changes,
  // so it never opens showing another record's filtered slice. Cancel any
  // pending debounce first (the controlling props changed) so a queued search
  // from the previous open/config can't fire after this reset and silently
  // re-filter behind an input that looks empty.
  useEffect(() => {
    if (!isOpen) return
    cancelDebouncedSearch()
    setSearchInput('')
    setSearch('')
    setPage(1)
  }, [isOpen, configurationId, cancelDebouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const locations = data?.locations ?? []
  const totalPages = data?.pagination.totalPages ?? 0
  const countText =
    locationsCount === 1
      ? t('count_one')
      : t('count_other', { count: locationsCount })

  const isEmpty = !isFetching && !isError && locations.length === 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      closeType="small"
      zIndex={MODAL_Z_INDEX}
    >
      <div
        className={`p-xl d-flex flex-column ${css({
          width: CONTENT_WIDTH,
          maxWidth: '100%',
        })}`}
      >
        <Text.Header3 tag="h3" className="m-0">
          {t('title', { label })}
        </Text.Header3>
        <Text.BodyText4 color="grey70" className="mt-xs mb-m">
          {countText}
        </Text.BodyText4>

        <FormControl
          type="text"
          value={searchInput}
          onChange={(e: React.FormEvent<FormControl>) =>
            handleSearchChange((e.target as HTMLInputElement).value)
          }
          placeholder={t('search_placeholder')}
          aria-label={t('search_aria_label')}
          className={css({ fontSize: '16px' })}
        />

        <div
          className={css({
            marginTop: '16px',
            maxHeight: LIST_MAX_HEIGHT,
            overflowY: 'auto',
          })}
        >
          {isError && (
            <Notification kind="negative" size="full">
              {t('error')}
            </Notification>
          )}

          {isFetching && (
            <div className={css({ textAlign: 'center', padding: '24px 0' })}>
              <Spinner isLoading size="2x" />
            </div>
          )}

          {isEmpty && (
            <Text.BodyText4
              color="grey70"
              className={css({ display: 'block', padding: '24px 0' })}
            >
              {t('empty')}
            </Text.BodyText4>
          )}

          {!isFetching &&
            !isError &&
            locations.map(location => (
              <div
                key={location.id}
                className={css({
                  padding: ROW_PADDING,
                  borderBottom: `1px solid ${palette.grey20}`,
                })}
              >
                <Text.BodyText5 bold color="grey90" className="d-block">
                  {location.name}
                </Text.BodyText5>
                <Text.BodyText5
                  color="grey70"
                  className={css({ display: 'block', marginTop: '2px' })}
                >
                  {formattedAddress(location)}
                </Text.BodyText5>
              </div>
            ))}
        </div>

        {totalPages > 1 && (
          <div className={css({ marginTop: '16px' })}>
            <Pagination
              currentPage={page}
              numPages={totalPages}
              onChangePage={setPage}
            />
          </div>
        )}

        <div className="d-flex justify-content-end mt-l">
          <Button onClick={onClose}>{t('close')}</Button>
        </div>
      </div>
    </Modal>
  )
}

export default LocationsModal
